import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import axios from "axios";
import { registerDevice, pinLogin } from "../../services/pos";

export type DeviceInfo = { id: string; uuid: string; name?: string };
export type Employee = {
  id: number;
  name: string;
  role: "waiter" | "bartender" | "cashier" | "kitchen" | "manager" | "admin";
};

const IDLE_TIMEOUT_MS = 180_000;

axios.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem("device_token");
    if (t) {
      if (!config.headers["Authorization"])
        config.headers["Authorization"] = `Bearer ${t}`;
      (config.headers as any)["pos-token"] = t;
      (config.headers as any)["X-Pos-Token"] = t;
    }
  } catch {}
  return config;
});

export function useAuthDevice({ autoLoginDeviceOnly = true }) {
  const [device, setDevice] = useState<DeviceInfo | null>(() => {
     try {
      return localStorage.getItem("pos.device")
        ? JSON.parse(localStorage.getItem("pos.device") || "null")
        : null;
    } catch {
      console.error("Failed to parse device info from localStorage");
      return null;
    }
  });
  const [deviceUUID, setDeviceUUID] = useState(device?.uuid || "");
  const [deviceID] = useState(device?.id || "");
  const [deviceName, setDeviceName] = useState(device?.name || "");
  const [deviceModal, setDeviceModal] = useState(
    !localStorage.getItem("device_token")
  );

  const ensureDevice = useCallback(async () => {
    const uuid = (deviceUUID || "").trim();
    if (!uuid || uuid.length < 8) {
      message.warning("Unesi ispravan UUID.");
      return;
    }

    try {
      const res = await registerDevice({ uuid });
      const toSave: DeviceInfo = { id: deviceID, uuid, name: deviceName || undefined };
      localStorage.setItem("pos.device", JSON.stringify(res.device || toSave));
      setDevice(toSave);
      setDeviceModal(false);
      message.success("Uređaj povezan (UUID).");
    } catch {
      message.error("Povezivanje nije uspjelo. Provjeri UUID ili API URL.");
    }
    //eslint-disable-next-line
  }, [deviceUUID, deviceName]);

  const [employee, setEmployee] = useState<Employee | null>(() => {
    try {
      return JSON.parse(localStorage.getItem("pos.employee") || "null");
    } catch {
      return null;
    }
  });
  const isLoggedIn = !!employee;
  const idleRef = useRef<number | null>(null);
  const bumpIdle = useCallback(() => {
    const timeToLock = Number(localStorage.getItem("lock_duration_sec") || IDLE_TIMEOUT_MS);
    if (!isLoggedIn) return;
    if (idleRef.current) window.clearTimeout(idleRef.current);
    idleRef.current = window.setTimeout(() => {
      localStorage.removeItem("pos.employee");
      setEmployee(null);
      console.log(timeToLock, "ms of inactivity, logging out");
      message.info("Sesija istekla — prijavi se ponovo.");
    }, timeToLock);
  }, [isLoggedIn]);
  useEffect(() => {
    const ev = () => bumpIdle();
    window.addEventListener("click", ev);
    window.addEventListener("keydown", ev);
    window.addEventListener("mousemove", ev);
    return () => {
      window.removeEventListener("click", ev);
      window.removeEventListener("keydown", ev);
      window.removeEventListener("mousemove", ev);
    };
  }, [bumpIdle]);

  const doLogin = useCallback(
    async (pin: string) => {
      try {
        if (!localStorage.getItem("device_token")) {
          message.warning("Uređaj nije povezan. Unesi UUID da dobiješ token.");
          setDeviceModal(true);
          throw new Error("no-device-token");
        }
        const data = await pinLogin(pin);
        const emp: Employee = data.employee as any;
        localStorage.setItem("pos.employee", JSON.stringify(emp));
        //reload refresh location
        setEmployee(emp);
        bumpIdle();
        message.success(`Prijavljen: ${emp.name}`);
        window.location.reload();
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || "";
        if (
          msg.includes('Attempt to read property "outlet_id" on null') ||
          e?.response?.status === 401
        ) {
          message.error(
            "Nevažeći ili istekao token uređaja. Poveži uređaj ponovo (UUID)."
          );
          localStorage.removeItem("device_token");
          setDeviceModal(true);
          return;
        }
        if (msg.includes("Target class [pos.token] does not exist")) {
          message.error(
            "Server očekuje 'pos-token' header. Pokušali smo ga poslati — ponovi prijavu."
          );
          return;
        }
        message.error("Pogrešan PIN ili greška na serveru.");
        throw new Error("login-failed");
      }
    },
    [bumpIdle]
  );

  const doLogout = useCallback(() => {
    localStorage.removeItem("pos.employee");
    setEmployee(null);
  }, []);

  return {
    device,
    deviceUUID,
    setDeviceUUID,
    deviceName,
    setDeviceName,
    deviceModal,
    setDeviceModal,
    ensureDevice,
    employee,
    isLoggedIn,
    doLogin,
    doLogout,
  };
}
